/**
 * Helpers for the per-parade-year "rolling" opt-out flag on a Member.
 *
 * Storage shape: `Member.rolling_by_year` is a map from a parade year
 * (string, e.g. "2026") to 'Yes' | 'No'. Default (absent entry) is 'Yes'
 * for normal members — they are considered rolling unless they explicitly
 * opt out (paying dues is treated as implicit "Yes"). For redshirts there
 * is no payment, so the default is 'No' and callers should prompt the
 * member to make an explicit choice. This flag is used purely for
 * email-comms targeting; dues are NOT waived by it.
 */

export type MemberRollingStatus = 'Yes' | 'No'
export type MemberRollingByYear = Record<string, MemberRollingStatus>

export interface MemberRollingLike {
  rolling_by_year?: MemberRollingByYear | null
}

/**
 * Return the explicit rolling status for a member for a given parade
 * year, or `undefined` if nothing has been set. Useful for distinguishing
 * "unset" from a defaulted value (e.g. to decide whether to prompt a
 * redshirt for an explicit choice).
 */
export function getMemberRollingStatusRaw(
  m: MemberRollingLike | null | undefined,
  year: string | number | null | undefined,
): MemberRollingStatus | undefined {
  if (!m || year === null || year === undefined) return undefined
  const v = m.rolling_by_year?.[String(year)]
  return v === 'Yes' || v === 'No' ? v : undefined
}

/**
 * Resolve the rolling status for a member for a given parade year,
 * applying `defaultValue` (default 'Yes', i.e. opt-out for normal
 * members) when nothing is set. Pass `'No'` for redshirts.
 */
export function getMemberRollingForYear(
  m: MemberRollingLike | null | undefined,
  year: string | number | null | undefined,
  defaultValue: MemberRollingStatus = 'Yes',
): MemberRollingStatus {
  return getMemberRollingStatusRaw(m, year) ?? defaultValue
}

/**
 * Build a new per-year map with `value` merged under `year`, preserving any
 * other years already present.
 */
export function mergeMemberRollingForYear(
  m: MemberRollingLike | null | undefined,
  year: string | number,
  value: MemberRollingStatus,
): MemberRollingByYear {
  const existing = m?.rolling_by_year
  const base: MemberRollingByYear = existing ? { ...existing } : {}
  base[String(year)] = value
  return base
}
